/**
 * uuid generator
 * @returns {String} - uuid string
 */
function generateUuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        // eslint-disable-next-line no-bitwise
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    )
}

/**
 * @returns {String} - OS type (windows phone, android, ios, windows, macOS, unknown)
 */
function getOsType() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
        return 'windows phone'
    }

    if (/android/i.test(userAgent)) {
        return 'android'
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios'
    }

    if (/windows/i.test(userAgent)) {
        return 'windows'
    }

    if (/Mac OS/i.test(userAgent)) {
        return 'macOS'
    }

    if (/CrOS/i.test(userAgent)) {
        return 'chromeOS'
    }

    if (/linux/i.test(userAgent)) {
        return 'linux'
    }

    return 'unknown'
}

/**
 * @returns {boolean} `true` if the viewport is a mobile viewport, `false` otherwise.
 * */
function isMobileViewport() {
    const desktopViewport = 1280
    const screenWidth = window.innerWidth

    return screenWidth < desktopViewport
}

/**
 * @returns {String} - mobile/desktop - based on viewport resolution
 * */
function getPlatform() {
    return isMobileViewport() ? 'web_mobile' : 'web_desktop'
}

/**
 * Retrieves an enumeration value representing the platform based on the current viewport.
 *
 * @returns {number} An enumeration value indicating the platform:
 * - 3 for mobile platform
 * - 4 for desktop platform
 */
function getPlatformEnum() {
    // IDs are defined by the data analyst team
    const platform = {
        mobile: 3,
        desktop: 4,
    }

    return isMobileViewport() ? platform.mobile : platform.desktop
}

function onVisibilityChange(callback) {
    let visible = true

    if (!callback) {
        throw new Error('no callback given')
    }

    function focused() {
        if (!visible) {
            callback((visible = true))
        }
    }

    function unfocused() {
        if (visible) {
            callback((visible = false))
        }
    }

    // Standards:
    if ('hidden' in document) {
        visible = !document.hidden

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                unfocused()
            } else {
                focused()
            }
        })
    }

    if ('mozHidden' in document) {
        visible = !document.mozHidden

        document.addEventListener('mozvisibilitychange', () => {
            if (document.mozHidden) {
                unfocused()
            } else {
                focused()
            }
        })
    }

    if ('webkitHidden' in document) {
        visible = !document.webkitHidden

        document.addEventListener('webkitvisibilitychange', () => {
            if (document.webkitHidden) {
                unfocused()
            } else {
                focused()
            }
        })
    }

    if ('msHidden' in document) {
        visible = !document.msHidden

        document.addEventListener('msvisibilitychange', () => {
            if (document.msHidden) {
                unfocused()
            } else {
                focused()
            }
        })
    }
}

/**
 * Retrieves the current domain name without the .com, .net, etc. suffix.
 * @returns The current domain name as a string.
 */
function getCurrentDomain() {
    try {
        const hostname = window.location.hostname.split('.')

        return hostname.slice(0, hostname.length - 1).join('.')
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)

        return 'superbet'
    }
}

export {
    generateUuidv4,
    getOsType,
    isMobileViewport,
    getPlatform,
    getPlatformEnum,
    onVisibilityChange,
    getCurrentDomain,
}
